import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiSearch, FiCalendar, FiPlus, FiX } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';

const StockDistribution = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [searchTerm, setSearchTerm] = useState('');
    const [distributions, setDistributions] = useState([]);
    const [persons, setPersons] = useState([]);
    const [stockItems, setStockItems] = useState([]);

    // Form State
    const initialFormState = {
        distributionNo: '',
        voucherNo: '', // Slip No
        distributionDate: new Date().toISOString().split('T')[0],
        personId: '',
        personName: '',
        totalAmount: 0,
        remarks: '',
        // Item Entry State
        currentItem: {
            itemId: '',
            itemName: '',
            measurement: '',
            currentStock: 0,
            quantity: '',
            rate: '',
            amount: ''
        },
        items: [], // Array of added items
    };
    const [formData, setFormData] = useState(initialFormState);

    // Fetch Initial Data
    useEffect(() => {
        // Distributions
        const distRef = ref(database, 'Grampanchayat/Panipuravtha/StockDistribution');
        onValue(distRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) {
                Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            }
            setDistributions(loaded);
        });

        // Persons (Beneficiaries)
        const personsRef = ref(database, 'Grampanchayat/Panipuravtha/Persons');
        onValue(personsRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            setPersons(loaded);
        });

        // Stock Items
        const stockRef = ref(database, 'Grampanchayat/Panipuravtha/Stock');
        onValue(stockRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            setStockItems(loaded);
        });
    }, []);

    const handleAddItem = () => {
        if (!formData.currentItem.itemId || !formData.currentItem.quantity) {
            alert('कृपया वस्तू आणि संख्या भरा');
            return;
        }

        // Stock Availability Check (Mock check as currentStock isn't fully managed implies reading from stock node directly or maintained locally)
        // For now, assuming currentStock in currentItem is valid
        if (parseFloat(formData.currentItem.quantity) > parseFloat(formData.currentItem.currentStock)) {
             alert(`पुरेशी शिल्लक नाही! उपलब्ध साठा: ${formData.currentItem.currentStock}`);
             return;
        }
        
        const newItem = {
            ...formData.currentItem,
            total: parseFloat(formData.currentItem.amount) || 0
        };

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem],
            totalAmount: (parseFloat(prev.totalAmount) || 0) + newItem.total,
            currentItem: initialFormState.currentItem // Reset item entry
        }));
    };

    const handleRemoveItem = (index) => {
        const itemToRemove = formData.items[index];
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index),
            totalAmount: prev.totalAmount - itemToRemove.total
        }));
    };

    const handleSave = () => {
        if (!formData.personId || formData.items.length === 0) {
            alert('कृपया व्यक्ती निवडा आणि किमान एक वस्तू जोडा');
            return;
        }

        const distRef = ref(database, 'Grampanchayat/Panipuravtha/StockDistribution');
        const newDist = {
            ...formData,
            createdAt: new Date().toISOString()
        };

        if (formData.id) {
            // Update logic here
        } else {
            push(distRef, newDist)
                .then(() => {
                    alert('साठा वाटप यशस्वीरित्या साठवले');
                    setFormData(initialFormState);
                    setViewMode('list');
                })
                .catch(err => {
                    console.error(err);
                    alert('त्रुटी आली');
                });
        }
    };

    const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);

    // Filter Logic for Modals
    const [modalSearch, setModalSearch] = useState('');

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 font-poppins">
            {/* Person Selection Modal */}
            {isPersonModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden animate-fade-in-up">
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="font-bold text-lg">व्यक्ती निवड</h3>
                            <button onClick={() => setIsPersonModalOpen(false)} className="text-white hover:text-red-300 transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-4 bg-white p-2 border border-blue-200 rounded">
                                <label className="font-bold text-gray-700 whitespace-nowrap">शोधणे</label>
                                <input 
                                    type="text" 
                                    placeholder="नाव किंवा मोबाईल नं. भरा"
                                    className="w-full border border-gray-300 p-2 rounded outline-none focus:border-blue-500"
                                    onChange={(e) => setModalSearch(e.target.value)}
                                />
                            </div>
                            <div className="overflow-x-auto h-80 overflow-y-auto border border-gray-300">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-[#2c5282] text-white sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 border-r">नाव</th>
                                            <th className="px-4 py-2 border-r">मोबाईल</th>
                                            <th className="px-4 py-2">पत्ता</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {persons.filter(p => (p.firstName + ' ' + p.surname).toLowerCase().includes(modalSearch.toLowerCase())).length > 0 ? (
                                            persons.filter(p => (p.firstName + ' ' + p.surname).toLowerCase().includes(modalSearch.toLowerCase())).map((p, idx) => (
                                                <tr 
                                                    key={p.id} 
                                                    className="hover:bg-blue-100 cursor-pointer transition-colors odd:bg-white even:bg-gray-50"
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev, 
                                                            personId: p.id, 
                                                            personName: `${p.firstName} ${p.fatherName || ''} ${p.surname}`
                                                        }));
                                                        setIsPersonModalOpen(false);
                                                    }}
                                                >
                                                    <td className="px-4 py-2 border-r font-medium text-blue-800">{p.firstName} {p.fatherName} {p.surname}</td>
                                                    <td className="px-4 py-2 border-r">{p.mobileNo}</td>
                                                    <td className="px-4 py-2">{p.villageName}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="3" className="p-4 text-center text-gray-500">No records found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Item Selection Modal */}
            {isItemModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden animate-fade-in-up">
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="font-bold text-lg">वस्तु/मालाची यादी</h3>
                            <button onClick={() => setIsItemModalOpen(false)} className="text-white hover:text-red-300 transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-4 bg-white p-2 border border-blue-200 rounded">
                                <label className="font-bold text-gray-700 whitespace-nowrap">शोधणे</label>
                                <input 
                                    type="text" 
                                    placeholder="वस्तुचे नाव भरा."
                                    className="w-full border border-gray-300 p-2 rounded outline-none focus:border-blue-500"
                                    onChange={(e) => setModalSearch(e.target.value)}
                                />
                            </div>
                            <div className="overflow-x-auto h-80 overflow-y-auto border border-gray-300">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-[#2c5282] text-white sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 border-r">वस्तुचे नाव</th>
                                            <th className="px-4 py-2 border-r">शिल्लक संख्या</th>
                                            <th className="px-4 py-2">प्रकार</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {stockItems.filter(i => i.name.toLowerCase().includes(modalSearch.toLowerCase())).length > 0 ? (
                                            stockItems.filter(i => i.name.toLowerCase().includes(modalSearch.toLowerCase())).map((item) => (
                                                <tr 
                                                    key={item.id} 
                                                    className="hover:bg-blue-100 cursor-pointer transition-colors odd:bg-white even:bg-gray-50"
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev, 
                                                            currentItem: {
                                                                ...prev.currentItem, 
                                                                itemId: item.id, 
                                                                itemName: item.name,
                                                                measurement: item.measurementType || '',
                                                                currentStock: 100, // Mock stock for now if not in DB
                                                                rate: item.rate || 0
                                                            }
                                                        }));
                                                        setIsItemModalOpen(false);
                                                    }}
                                                >
                                                    <td className="px-4 py-2 border-r font-medium text-blue-800">{item.name}</td>
                                                    <td className="px-4 py-2 border-r font-bold text-green-600">100</td>
                                                    <td className="px-4 py-2">{item.type}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="3" className="p-4 text-center text-gray-500">No records found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg border border-blue-900 overflow-hidden">
                {/* Header */}
                <div className="bg-blue-800 text-white p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                         <button 
                            onClick={() => viewMode === 'form' ? setViewMode('list') : navigate(-1)} 
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors flex items-center justify-center mr-2"
                        >
                            <FiArrowLeft className="text-xl" />
                        </button>
                        <h1 className="text-xl font-bold">
                            {viewMode === 'list' ? 'साठा वाटप माहिती' : 'साठा वाटप व्यवहार माहिती'}
                        </h1>
                    </div>
                    {viewMode === 'list' && (
                        <button 
                            onClick={() => {
                                setFormData(initialFormState);
                                setViewMode('form');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-1.5 rounded font-bold shadow-sm transition-colors text-sm"
                        >
                            नवीन
                        </button>
                    )}
                </div>

                {viewMode === 'list' ? (
                    <>
                        <div className="p-4 border-b border-gray-200">
                             <div className="flex gap-4 items-center bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                                <label className="font-medium text-gray-700">व्यवहार दिनांक:</label>
                                <div className="flex items-center border border-gray-300 rounded bg-white px-2">
                                    <input type="date" className="p-1 outline-none text-sm" defaultValue={new Date().toISOString().split('T')[0]} />
                                </div>
                                <button className="bg-green-600 text-white px-4 py-1 rounded text-sm font-bold">दाखवा</button>
                             </div>

                             <div className="flex gap-2">
                                <div className="relative flex-grow">
                                    <input 
                                        type="text" 
                                        placeholder="शोधणे" 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
                                    />
                                </div>
                                <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold shadow-sm transition-colors">
                                    शोधणे
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-center border-collapse">
                                <thead className="bg-[#2c5282] text-white">
                                    <tr>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">व्यवहार दिनांक</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium w-1/3 text-left">व्यक्तीचे नाव</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">स्लिप क्र.</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">एकूण रक्कम</th>
                                        <th className="px-4 py-3 font-medium">पर्याय</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-300">
                                    {distributions.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-left py-4 px-4 text-gray-700 font-medium bg-white">No records found</td>
                                        </tr>
                                    ) : (
                                        distributions.map(item => (
                                            <tr key={item.id} className="hover:bg-blue-50">
                                                <td className="px-4 py-3 border-r">{item.distributionDate}</td>
                                                <td className="px-4 py-3 border-r text-left">{item.personName}</td>
                                                <td className="px-4 py-3 border-r">{item.voucherNo}</td>
                                                <td className="px-4 py-3 border-r">{item.totalAmount}</td>
                                                <td className="px-4 py-3">
                                                    <button className="bg-blue-700 text-white px-3 py-1 rounded text-xs mr-2">बदल</button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                         {/* Pagination */}
                         <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 flex justify-center gap-1">
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">|&lt;</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&lt;&lt;</button>
                             <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs border border-teal-400 font-bold">1</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&gt;&gt;</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&gt;|</button>
                        </div>
                    </>
                ) : (
                    <div className="p-4 bg-gray-50">
                        {/* Header Box */}
                        <div className="border border-blue-300 rounded p-4 bg-white mb-4 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                    <label>वाटप क्र.:</label>
                                    <input type="text" value="वाटप" disabled className="border p-1 rounded w-32 bg-gray-100" />
                                </div>
                                <div className="flex items-center gap-2 col-span-2">
                                     {/* Spacer or Type */}
                                </div>
                                <div className="flex items-center gap-2">
                                    <label>स्लिप नं.:</label>
                                    <input 
                                        type="text" 
                                        className="border p-1 rounded w-full" 
                                        placeholder="स्लिप नं. भरा" 
                                        value={formData.voucherNo}
                                        onChange={(e) => setFormData(prev => ({...prev, voucherNo: e.target.value}))}
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label>वाटप दिनांक:</label>
                                    <input 
                                        type="date" 
                                        className="border p-1 rounded w-full" 
                                        value={formData.distributionDate}
                                        onChange={(e) => setFormData(prev => ({...prev, distributionDate: e.target.value}))}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="w-32">व्यक्तीचे नाव:</label>
                                <div className="flex-grow flex gap-0">
                                    <input 
                                        type="text"
                                        className="w-full border border-blue-300 p-1.5 rounded-l outline-none bg-white"
                                        value={formData.personName}
                                        placeholder="व्यक्तीचे नाव निवडा"
                                        readOnly
                                    />
                                    <button 
                                        onClick={() => setIsPersonModalOpen(true)}
                                        className="bg-blue-800 text-white px-3 rounded-r hover:bg-blue-900 transition-colors"
                                    >
                                        ...
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            {/* Left: Item Entry */}
                            <div className="lg:col-span-4 border border-blue-500 rounded flex flex-col">
                                <div className="bg-blue-800 text-white p-2 text-center font-bold text-sm">वस्तुची माहिती</div>
                                <div className="p-4 bg-white flex-grow flex flex-col gap-3 text-sm">
                                    <div>
                                        <label className="block mb-1 text-gray-700">वस्तुचे नाव:</label>
                                        <div className="flex">
                                            <input 
                                                type="text"
                                                className="w-full border border-gray-300 p-1.5 rounded-l bg-white"
                                                value={formData.currentItem.itemName}
                                                placeholder="वस्तुचे नाव निवडा."
                                                readOnly
                                            />
                                            <button 
                                                onClick={() => setIsItemModalOpen(true)}
                                                className="bg-blue-800 text-white px-3 rounded-r hover:bg-blue-900 transition-colors"
                                            >
                                                ...
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block mb-1 text-gray-700">मोजमाप प्रकार:</label>
                                        <input type="text" className="w-full border border-gray-300 p-1.5 rounded bg-gray-50" readOnly value={formData.currentItem.measurement} />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">शिल्लक संख्या:</label>
                                        <div className="bg-blue-100 border border-blue-300 p-1.5 text-center font-bold text-blue-800">{formData.currentItem.currentStock || 0}</div>
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">संख्या:</label>
                                        <input 
                                            type="number" 
                                            className="w-full border border-blue-300 p-1.5 rounded" 
                                            placeholder="संख्या भरा." 
                                            value={formData.currentItem.quantity}
                                            onChange={(e) => {
                                                const qty = parseFloat(e.target.value) || 0;
                                                const rate = parseFloat(formData.currentItem.rate) || 0;
                                                setFormData(prev => ({
                                                    ...prev, 
                                                    currentItem: { ...prev.currentItem, quantity: e.target.value, amount: qty * rate }
                                                }));
                                            }}
                                        />
                                    </div>

                                    <button 
                                        onClick={handleAddItem}
                                        className="mt-2 bg-gradient-to-r from-green-600 to-green-700 text-white p-2 rounded font-bold shadow hover:from-green-700 hover:to-green-800 transition-all"
                                    >
                                        यादित भरा
                                    </button>
                                </div>
                            </div>

                            {/* Right: Items Table */}
                            <div className="lg:col-span-8 border border-blue-500 rounded overflow-hidden flex flex-col">
                                <div className="bg-[#2c5282] text-white flex text-xs font-bold p-2">
                                    <div className="w-8">#</div>
                                    <div className="flex-grow">वस्तुचे नाव</div>
                                    <div className="w-20 text-center">मोजमाप</div>
                                    <div className="w-16 text-center">संख्या</div>
                                    <div className="w-8">#</div>
                                </div>
                                <div className="bg-gray-100 flex-grow p-2">
                                    {formData.items.length === 0 ? (
                                        <div className="text-gray-500 text-sm italic">No records found</div>
                                    ) : (
                                        formData.items.map((item, idx) => (
                                            <div key={idx} className="flex text-sm p-2 border-b border-gray-300 bg-white">
                                                <div className="w-8">{idx + 1}</div>
                                                <div className="flex-grow">{item.itemName}</div>
                                                <div className="w-20 text-center">{item.measurement}</div>
                                                <div className="w-16 text-center">{item.quantity}</div>
                                                <div className="w-8 text-red-500 cursor-pointer" onClick={() => handleRemoveItem(idx)}><FiTrash2 /></div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                        
                         {/* Description */}
                         <div className="mt-4">
                            <label className="text-sm font-bold text-gray-700">शेरा:</label>
                            <textarea 
                                className="w-full border border-blue-300 rounded p-2 mt-1 focus:ring-1 focus:ring-blue-500 outline-none"
                                rows="2"
                                placeholder="शेरा भरा"
                                value={formData.remarks}
                                onChange={(e) => setFormData(prev => ({...prev, remarks: e.target.value}))}
                            ></textarea>
                         </div>

                        {/* Footer Buttons */}
                        <div className="flex justify-center gap-4 mt-4 bg-blue-900 p-3 rounded-b-lg text-white">
                            <button className="bg-green-600 px-6 py-1 rounded font-bold" onClick={handleSave}>साठविणे</button>
                            <button className="bg-red-500 px-6 py-1 rounded font-bold" onClick={() => setViewMode('list')}>रद्द करणे</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockDistribution;
